/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { connect } from 'react-redux';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import { Formik } from 'formik';
import { useParams } from 'react-router';
import i18n from 'i18n';
import BlockIcon from '@material-ui/icons/Block';
import AvTimerIcon from '@material-ui/icons/AvTimer';
import { Alert } from '@material-ui/lab';

import {
  getEnvironmentMonitoringRequest,
  editEnvironmentMonitoringRequest,
} from 'modules/environments/actions';
import {
  EnvironmentMonitoringProps,
  CPUBenchmarksSettings,
} from 'modules/environments/interfaces';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import AutoSwitch from 'components/AutoSwitch';
import Button from 'components/Button';
import ToggleCard from 'components/ToggleCard';
import InputAdornmentChipMonitoring from 'components/InputAdornmentChipMonitoring';
import GenericForm from 'skyone-lib/lib/GenericForm';
import TitleBehavior from 'containers/Environments/Configuration/FormBehavior/TitleBehavior';
import ExplanationHover from 'components/ExplanationHover';
import { isClientOperator } from 'utils/role';
import { RolesEnum } from 'modules/authenticate/interfaces';
import MonitoringHistory from './MonitoringHistory';
import schema from './schema';
import styles, {
  overrideSwitch,
  overrideTitleBehavior,
  overrideToggleCard,
} from './styles';

interface Props {
  environmentMonitoring: EnvironmentMonitoringProps;
  environmentName?: string;
  role?: RolesEnum;
  getEnvironmentMonitoringRequest: (props) => void;
  editEnvironmentMonitoringRequest: ({ id, values }) => void;
}

const initialCPUBenchmarkSettings: CPUBenchmarksSettings = {
  enabled: false,
  execution_interval_minutes: 30,
  duration_seconds: 10,
};

const Monitoring = ({
  environmentMonitoring,
  environmentName,
  role,
  getEnvironmentMonitoringRequest,
  editEnvironmentMonitoringRequest,
}: Props) => {
  const { id } = useParams<{ id: string }>();
  const [isMonitoringEnabled, setMonitoringEnabled] = React.useState(
    environmentMonitoring?.enabled
  );
  const classes = makeStyles(styles(isMonitoringEnabled))();

  React.useEffect(() => {
    getEnvironmentMonitoringRequest({ id });
  }, [getEnvironmentMonitoringRequest, id]);

  const onSubmit = values => {
    const adjustedValues = {
      ...values,
      enabled: isMonitoringEnabled,
      cpu_benchmarks: {
        ...values.cpu_benchmarks,
        execution_interval_minutes: values.cpu_benchmarks?.enabled
          ? values.cpu_benchmarks.execution_interval_minutes
          : null,
        duration_seconds: values.cpu_benchmarks?.enabled
          ? values.cpu_benchmarks.duration_seconds
          : null,
      },
    };

    editEnvironmentMonitoringRequest({
      id,
      values: adjustedValues,
    });
  };

  const handleToggle = (fieldKey, value, setFieldValue) => {
    setFieldValue(fieldKey, !value);
  };

  return (
    <>
      <Grid
        container
        justify="flex-start"
        alignItems="center"
        className={classes.buttons}
      >
        <Grid item xs={12}>
          <TitleBehavior
            overrideClasses={overrideTitleBehavior}
            title={i18n.t('MONITORING_KEY')}
            subtitle={i18n.t('MONITORING_PAGE.SUBTITLE_KEY')}
          />

          <Formik
            validationSchema={schema}
            initialValues={{
              ...environmentMonitoring,
              cpu_benchmarks: environmentMonitoring.cpu_benchmarks
                ? environmentMonitoring.cpu_benchmarks
                : initialCPUBenchmarkSettings,
            }}
            onSubmit={onSubmit}
          >
            {({ setFieldValue, submitForm, errors, values }) => (
              <>
                <Grid container alignItems="center">
                  <AutoSwitch
                    overrideClasses={overrideSwitch(isMonitoringEnabled)}
                    label={
                      <Typography className={classes.switchText}>
                        {i18n.t('MONITORING_PAGE.SWITCH_TEXT_KEY')}
                      </Typography>
                    }
                    id="toggle-activate-inactivate"
                    size="medium"
                    onChange={() => setMonitoringEnabled(!isMonitoringEnabled)}
                    checked={isMonitoringEnabled}
                  />
                </Grid>

                {isMonitoringEnabled && (
                  <Grid container spacing={2}>
                    <InputAdornmentChipMonitoring
                      gridXs={12}
                      label={i18n.t('MONITORING_PAGE.EMAILS_TO_ALERT_KEY')}
                      stateKey="monitoring_alerts_emails"
                      values={environmentMonitoring?.monitoring_alerts_emails}
                      setFieldValue={setFieldValue}
                    />
                    <Grid container spacing={2} className={classes.toggles}>
                      <ToggleSection
                        label={i18n.t('MONITORING_PAGE.OPERATING_TIME_KEY')}
                        icon={<AvTimerIcon className={classes.timeIcon} />}
                        active={
                          environmentMonitoring?.instances_operational_timing_enabled
                        }
                        setFieldValue={value =>
                          handleToggle(
                            'instances_operational_timing_enabled',
                            value,
                            setFieldValue
                          )
                        }
                      >
                        <Alert
                          className={classes.alert}
                          severity="warning"
                          color="info"
                        >
                          <Typography className={classes.textAlert}>
                            {i18n.t(
                              'MONITORING_PAGE.OPERATING_TIME_AUTO_LIMIT_KEY'
                            )}
                          </Typography>
                        </Alert>
                      </ToggleSection>
                      <Permission value="edit_block_instances_with_sessions_timeout">
                        <ToggleSection
                          label={i18n.t(
                            'BLOCK_INSTANCES_WITH_SESSIONS_TIMEOUT_KEY'
                          )}
                          icon={<BlockIcon className={classes.timeIcon} />}
                          active={
                            environmentMonitoring?.block_instances_with_sessions_timeout_enabled
                          }
                          setFieldValue={value =>
                            handleToggle(
                              'block_instances_with_sessions_timeout_enabled',
                              value,
                              setFieldValue
                            )
                          }
                        >
                          <Alert
                            className={classes.alert}
                            severity="warning"
                            color="info"
                          >
                            <Typography className={classes.textAlert}>
                              {i18n.t(
                                'BLOCK_INSTANCES_WITH_SESSIONS_TIMEOUT_ALERT_KEY'
                              )}
                            </Typography>
                          </Alert>
                        </ToggleSection>
                      </Permission>
                      {!isClientOperator(role) && (
                        <ToggleSection
                          label={i18n.t('BENCHMARK_INSTANCES_KEY')}
                          icon={<TrendingUpIcon className={classes.timeIcon} />}
                          active={
                            environmentMonitoring?.cpu_benchmarks?.enabled
                          }
                          setFieldValue={value =>
                            handleToggle(
                              'cpu_benchmarks.enabled',
                              value,
                              setFieldValue
                            )
                          }
                        >
                          <section className={classes.content}>
                            <Grid container className={classes.operatingGrid}>
                              <Grid
                                item
                                xs={12}
                                className={classes.operatingInput}
                              >
                                <Grid item xs={6}>
                                  <Typography
                                    className={classes.textFieldLabel}
                                  >
                                    {i18n.t('BENCHMARK_INTERVAL_KEY')}
                                  </Typography>
                                </Grid>

                                <GenericForm
                                  fields={values}
                                  values={values}
                                  screenFields={[
                                    {
                                      gridXs: 4,
                                      type: 'number',
                                      label: i18n.t('MINUTES_KEY'),
                                      stateKey:
                                        'cpu_benchmarks.execution_interval_minutes',
                                    },
                                  ]}
                                  setFieldValue={setFieldValue}
                                  errors={errors}
                                />
                                <Grid item xs={2} style={{ marginLeft: 8 }}>
                                  <ExplanationHover
                                    title={i18n.t(
                                      'BENCHMARK_INTERVAL_TOOLTIP_KEY'
                                    )}
                                  />
                                </Grid>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                className={classes.operatingInput}
                              >
                                <Grid item xs={6}>
                                  <Typography
                                    className={classes.textFieldLabel}
                                  >
                                    {i18n.t('BENCHMARK_DURATION_KEY')}
                                  </Typography>
                                </Grid>

                                <GenericForm
                                  fields={values}
                                  values={values}
                                  screenFields={[
                                    {
                                      gridXs: 4,
                                      label: i18n.t('SECONDS_KEY'),
                                      stateKey:
                                        'cpu_benchmarks.duration_seconds',
                                      isSelect: true,
                                      options: [
                                        {
                                          value: 10,
                                          label: '10',
                                        },
                                        {
                                          value: 15,
                                          label: '15',
                                        },
                                        {
                                          value: 30,
                                          label: '30',
                                        },
                                        {
                                          value: 60,
                                          label: '60',
                                        },
                                      ],
                                    },
                                  ]}
                                  setFieldValue={setFieldValue}
                                  errors={errors}
                                />
                                <Grid item xs={2} style={{ marginLeft: 8 }}>
                                  <ExplanationHover
                                    title={i18n.t(
                                      'BENCHMARK_DURATION_TOOLTIP_KEY'
                                    )}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </section>
                        </ToggleSection>
                      )}
                    </Grid>
                  </Grid>
                )}
                <Grid container justify="flex-end" className={classes.buttons}>
                  <Button
                    id="submit-button"
                    onClick={submitForm}
                    submitButton
                    disabled={Object.keys(errors).length !== 0}
                  >
                    {i18n.t('SAVE_KEY')}
                  </Button>
                </Grid>
              </>
            )}
          </Formik>
        </Grid>
      </Grid>
      <MonitoringHistory environmentName={environmentName} />
    </>
  );
};

const ToggleSection = ({ label, icon, active, setFieldValue, children }) => (
  <Grid item xs={6}>
    <ToggleCard
      overrideClasses={overrideToggleCard}
      active={active}
      title={label}
      icon={icon}
      getActive={value => setFieldValue(value)}
    >
      {children}
    </ToggleCard>
  </Grid>
);

const mapStateToProps = ({
  environments: { environmentMonitoring },
  authenticate: { data },
}: ApplicationState) => ({
  environmentMonitoring,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getEnvironmentMonitoringRequest,
  editEnvironmentMonitoringRequest,
})(Monitoring);
